/**
 * Shared authentication utilities
 * Ensures consistent authentication checks across components
 */

/**
 * Check if user is authenticated
 * Uses the same logic as Conversation component: authenticated if auth.isAuthenticated OR has token
 *
 * @param isAuthenticated - From useAuth hook's isAuthenticated state
 * @param token - From useAuth hook's token state
 * @param hasStoredToken - Whether a token exists in localStorage (for client-side checks)
 * @returns true if user is considered authenticated
 */
export const isUserAuthenticated = (
  isAuthenticated: boolean,
  token: string | null,
  hasStoredToken?: boolean,
): boolean => {
  // If we have explicit authentication state, use it
  if (isAuthenticated) {
    return true
  }

  // Check for token from hook
  if (token) {
    return true
  }

  // Check localStorage if provided (client-side only)
  if (hasStoredToken !== undefined) {
    return hasStoredToken
  }

  // Fallback: check localStorage directly (client-side only)
  if (typeof window !== 'undefined') {
    return !!localStorage.getItem('token')
  }

  return false
}

/**
 * Get stored token from localStorage (client-side only)
 * @returns token string or null
 */
export const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  return localStorage.getItem('token')
}

/**
 * Check if a token exists in localStorage (client-side only)
 * @returns true if token exists
 */
export const hasStoredToken = (): boolean => {
  return !!getStoredToken()
}
